import type { InitializeSessionResponse } from '@/types/responses/initializeSessionResponse'
import type { CoverageOptionsResponse } from '@/types/responses/coverageOptionsResponse'
import type { CoverageSummary } from '@/types/coverage/coverageSummary'
import type { AddPetRequest } from '@/types/requests/addPetRequest'
import type { FinalReview } from '~/types/coverage/finalReview'
import { isDefined } from '@vueuse/core'

export const useAddPetReduceCoverageStore = defineStore('aprc', () => {
  const policyStore = usePolicyStore()
  const { selectedPolicy } = storeToRefs(policyStore)

  // State
  const coverageOptions = ref<CoverageOptionsResponse | null>(null)
  const coverageSummary = ref<{ coverageSummary: CoverageSummary } | null>(null)
  const finalReview = ref<FinalReview | null>(null)
  const selectedPetId = ref<string | undefined | null>('')
  const UseRenewal = ref<boolean | undefined | null>(null)
  const showPolicyRenewalModal = ref<boolean>(false)

  let currentUpdateCall: { cancel: () => void } | null = null

  const session = ref<InitializeSessionResponse | null>(null)

  // Actions
  async function addPet(payload: AddPetRequest) {
    const optionsReturn = (await useApi(
      `coverage/addpet`,
      {
        method: 'POST',
        body: { ...payload }
      },
      'page',
      true
    ).response) as CoverageOptionsResponse

    coverageOptions.value = optionsReturn

    const petSummary = optionsReturn.coverageSummary.pets
    selectedPetId.value = payload.petId
      ? petSummary.find((pet) => pet.id === payload.petId)?.id
      : petSummary[petSummary.length - 1]?.id
  }

  async function fetchCoverageOptions(petId?: string) {
    coverageOptions.value = (await useApi(
      `coverage/getcoverageoptions?PetId=${petId || selectedPetId.value}&PolicyId=${selectedPolicy.value.policyId}`,
      {},
      'page',
      true
    ).response) as CoverageOptionsResponse
  }

  async function updateCoverage(key: string, value: string | boolean | number) {
    if (currentUpdateCall) {
      currentUpdateCall.cancel()
      currentUpdateCall = null
    }

    if (!isDefined(value) || !isDefined(selectedPetId.value) || !isDefined(coverageOptions.value)) return

    const newCoverage = coverageOptions.value.coverageSummary.pets.find(
      (pet) => pet.id === selectedPetId.value
    )?.newPetCoverage

    const petCoverage = {
      ...newCoverage,
      [key]: value
    }

    try {
      const apiCall = useApi(
        'coverage/updatecoverage',
        {
          method: 'POST',
          body: {
            policyId: selectedPolicy.value.policyId,
            petId: selectedPetId.value,
            petCoverage
          }
        },
        'page',
        true
      )

      currentUpdateCall = apiCall

      coverageOptions.value.coverageSummary = ((await apiCall.response) as CoverageOptionsResponse).coverageSummary

      currentUpdateCall = null
    } catch (e: any) {
      // Only log error if it's not an abort error
      if (e.name !== 'AbortError') {
        console.error(e)
      }
    }
  }

  async function fetchCoverageSummary() {
    try {
      const summary = (await useApi(
        `coverage/getcoveragesummary?PolicyId=${selectedPolicy.value.policyId}`,
        {},
        'page',
        true
      ).response) as {
        coverageSummary: CoverageSummary
      }

      coverageSummary.value = summary
    } catch (e: any) {
      console.error(e.message)
    }
  }

  async function fetchFinalReview() {
    try {
      const review = (await useApi(`coverage/finalreview?PolicyId=${selectedPolicy.value.policyId}`, {}, 'page', true)
        .response) as FinalReview

      finalReview.value = review
    } catch (e: any) {
      console.error(e.message)
      throw new Error(e)
    }
  }

  async function finalizeCoverage() {
    try {
      const changes = await useApi(
        'coverage/finalizechanges',
        {
          method: 'POST',
          body: {
            policyId: selectedPolicy.value.policyId
          },
          suppressGlobalErrorModal: false
        },
        'page',
        true
      ).response

      policyStore.refreshPolicyStatuses()

      return changes
    } catch (e: any) {
      console.error(e.message)
      throw new Error(e)
    }
  }

  async function removePet(petId: string) {
    try {
      await useApi(
        'coverage/removePet',
        {
          method: 'POST',
          body: {
            policyId: selectedPolicy.value.policyId,
            petId
          }
        },
        'page',
        true
      ).response

      if (petId === selectedPetId.value) {
        selectedPetId.value = null
      }

      const summary = (await useApi(
        `coverage/getcoveragesummary?PolicyId=${selectedPolicy.value.policyId}`,
        {},
        'page',
        true
      ).response) as {
        coverageSummary: CoverageSummary
      }
      summary.coverageSummary.pets = summary.coverageSummary.pets.filter((pet) => pet.id !== petId)
      coverageSummary.value = summary
    } catch (e: any) {
      // Only log error if it's not an abort error
      if (e.name !== 'AbortError') {
        console.error(e)
      }
    }
  }

  async function updateUseRenewalPolicy(useRenewal: boolean) {
    await useApi(
      `coverage/SetSessionToUseRenewalPolicy`,
      {
        method: 'PUT',
        body: {
          policyId: selectedPolicy.value.policyId,
          useRenewal
        }
      },
      'dialog',
      true
    ).response
    UseRenewal.value = useRenewal
    showPolicyRenewalModal.value = false
  }

  async function setSession() {
    session.value = (await useApi(
      `coverage/initializesession`,
      {
        method: 'POST',
        body: { policyId: selectedPolicy.value.policyId },
        suppressGlobalErrorModal: false
      },
      'page',
      true
    ).response) as InitializeSessionResponse

    // Update the state and show the PolicyRenewal modal
    // if the session contains a renewalNoticeDto value
    showPolicyRenewalModal.value = isDefined(session?.value?.renewalNoticeDto)
  }

  function getCoverageOptions() {
    return coverageOptions.value
  }

  function getCoverageSummary() {
    return coverageSummary.value
  }

  function getSession() {
    return session.value
  }

  function getSelectedPetId() {
    return selectedPetId.value
  }

  function getTotalDiscountAmount() {
    return (
      coverageOptions.value?.coverageSummary?.discounts?.reduce(
        (total, discount) => total + Number(discount.amount || 0),
        0
      ) || 0
    )
  }

  function getFinalReview() {
    return finalReview.value
  }

  function getUseRenewal() {
    return UseRenewal.value
  }

  function setUseRenewal(value: boolean | undefined | null) {
    UseRenewal.value = value
  }

  function setSelectedPetId(petId?: string) {
    selectedPetId.value = petId
  }

  function $reset() {
    coverageOptions.value = null
    coverageSummary.value = null
    selectedPetId.value = null
    currentUpdateCall = null
    session.value = null
    finalReview.value = null
    showPolicyRenewalModal.value = false
    UseRenewal.value = null
  }

  async function deleteSession() {
    try {
      await useApi(
        `coverage/deletesession?PolicyId=${selectedPolicy.value.policyId}`,
        { method: 'DELETE' },
        'page',
        true
      ).response
    } catch (error) {
      console.error(error)
    }
  }

  function getShowPolicyRenewalModal() {
    return showPolicyRenewalModal.value
  }

  async function setShowPolicyRenewalModal(value: boolean) {
    showPolicyRenewalModal.value = value
  }

  return {
    // State
    coverageOptions,
    coverageSummary,
    selectedPetId,
    session,
    finalReview,
    UseRenewal,
    showPolicyRenewalModal,

    // Actions
    addPet,
    updateCoverage,
    removePet,
    updateUseRenewalPolicy,
    setSession,
    $reset,
    fetchCoverageOptions,
    fetchCoverageSummary,
    fetchFinalReview,
    finalizeCoverage,
    deleteSession,

    // Getter
    getSelectedPetId,
    getCoverageOptions,
    getCoverageSummary,
    getSession,
    getFinalReview,
    getUseRenewal,
    getShowPolicyRenewalModal,
    getTotalDiscountAmount,

    // Setters
    setSelectedPetId,
    setShowPolicyRenewalModal,
    setUseRenewal
  }
})
